import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, catchError, of, throwError } from 'rxjs';
import { BaySeating } from '../models/bay-seating.interface';
import { SeatHold } from '../models/seat-hold.interface';
import { EventSeatMap } from '../models/event-seat-map.interface';
import { EventModel, EventType } from '../models/event.interface';
import {
  AddToCartRequest,
  AddToCartResponse,
  Cart,
  CartItem,
} from '../models/cart.interface';
import {
  Address,
  DeliveryLocation,
  initiatePaymentReq,
  Order,
  PaginationResponse,
  RsvpDetails,
  Session,
  MerchantPosTransaction,
  TicketWallet,
  WalletTopUpRequest,
  WishlistItem,
} from '../models';
import { EventTicketType, TicketTypeAddOn } from '../models/event-ticket-type.interface';
import { EventField } from '../models/event-field-type.interface';
import { OrderFee } from '../models/order-fees';
import { Ticket } from '../models/tickets';
import { Microservice } from '@hypenation/shared';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly apiUrl: string = environment.apiUrl;

  constructor(private readonly _http: HttpClient) {}

  public getAllActiveEventsPaginated(
    page = 0,
    size = 24,
    sort?: string
  ): Observable<HttpResponse<PaginationResponse<EventModel[]>>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<PaginationResponse<EventModel[]>>(
        `${this.apiUrl}/${
          Microservice.ETICKETING
        }/opn/v1/events/active?page=${page}&size=${size}${sort ? sort : ''}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getAllPastEventsPaginated(
    page = 0,
    size = 24,
    sort?: string
  ): Observable<HttpResponse<PaginationResponse<EventModel[]>>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<PaginationResponse<EventModel[]>>(
        `${this.apiUrl}/${
          Microservice.ETICKETING
        }/opn/v1/events/past-events?page=${page}&size=${size}${
          sort ? sort : ''
        }`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getActivePrivateEvents(
    org: string,
    page = 0,
    size = 16,
    sort?: string
  ): Observable<HttpResponse<PaginationResponse<EventModel[]>>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<PaginationResponse<EventModel[]>>(
        `${this.apiUrl}/${
          Microservice.ETICKETING
        }/opn/v1/events/active/private?page=${page}&size=${size}&org=${org}${
          sort ? `&sort=${sort}` : ''
        }`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getPastPrivateEvents(
    org: string,
    page = 0,
    size = 16,
    sort?: string
  ): Observable<HttpResponse<PaginationResponse<EventModel[]>>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<PaginationResponse<EventModel[]>>(
        `${this.apiUrl}/${
          Microservice.ETICKETING
        }/opn/v1/events/past-events/private?page=${page}&size=${size}&org=${org}${
          sort ? `&sort=${sort}` : ''
        }`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getComingSoonEvents(
    page = 0,
    size = 24,
    sort?: string
  ): Observable<HttpResponse<PaginationResponse<EventModel[]>>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<PaginationResponse<EventModel[]>>(
        `${this.apiUrl}/${
          Microservice.ETICKETING
        }/opn/v1/events/coming-soon?page=${page}&size=${size}${
          sort ? `&sort=${sort}` : ''
        }`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getFeaturedEvents(
    page = 0,
    size = 10
  ): Observable<HttpResponse<PaginationResponse<EventModel[]>>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<PaginationResponse<EventModel[]>>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/events/featured?page=${page}&size=${size}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getAllActiveEventsFilteredPaginated(
    page = 0,
    size = 10,
    search?: string,
    sort?: string
  ): Observable<HttpResponse<PaginationResponse<EventModel[]>>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<PaginationResponse<EventModel[]>>(
        `${this.apiUrl}/${
          Microservice.ETICKETING
        }/opn/v1/events/active?page=${page}&size=${size}&q=name:like:${search},eventType.name:like:${search},location.description:like:${search}${
          sort ? sort : ''
        }`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getAllActiveEventsByCategoryPaginated(
    page = 0,
    size = 10,
    search?: string,
    sort?: string
  ): Observable<HttpResponse<PaginationResponse<EventModel[]>>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<PaginationResponse<EventModel[]>>(
        `${this.apiUrl}/${
          Microservice.ETICKETING
        }/opn/v1/events/active?page=${page}&size=${size}&q=eventType.name:eq:${search}${
          sort ? sort : ''
        }`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getEventBySlug(slug: string): Observable<HttpResponse<EventModel>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<EventModel>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/events/slug?slug=${slug}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }
  public getEventByShareToken(
    slug: string,
    token: string
  ): Observable<HttpResponse<EventModel>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<EventModel>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/events/share/${slug}/${token}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getEventByUid(uid: string): Observable<HttpResponse<EventModel>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<EventModel>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/events/${uid}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getAllEventTypes(): Observable<HttpResponse<EventType[]>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<EventType[]>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/event-types/all`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getAllEventTicketTypesByEventUuid(
    uuid: string
  ): Observable<HttpResponse<EventTicketType[]>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<EventTicketType[]>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/events/${uuid}/ticket-types`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getEventTicketTypeByUid(
    uid: string
  ): Observable<HttpResponse<EventTicketType>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<EventTicketType>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/ticket-types/${uid}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * The venue diagram and per-bay price and availability for an event, or null when the event has no
   * seat map: no venue chosen, or its venue has no diagram yet. Any other failure is thrown, so a
   * caller can tell "no map" from "could not load it".
   */
  public getEventSeatMap(eventUid: string): Observable<EventSeatMap | null> {
    return this._http
      .get<EventSeatMap>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/events/${eventUid}/seat-map`,
        {
          headers: new HttpHeaders({ Accept: 'application/json' }),
          withCredentials: false,
        }
      )
      .pipe(
        catchError((error: { status?: number }) =>
          error?.status === 404 ? of(null) : throwError(() => error)
        )
      );
  }

  /**
   * The rows and seats of one bay, front to back, with each seat's status. Not deployed yet (backend
   * branch feat/venue-bay-layouts): nothing calls this on a live API until the seat booking flag is
   * turned on. 404 means this bay has no seating layout; it is returned as null so the caller can tell
   * that from "could not load it".
   */
  public getBaySeating(eventUid: string, bayCode: string): Observable<BaySeating | null> {
    return this._http
      .get<BaySeating>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/events/${eventUid}/seat-map/bays/${encodeURIComponent(bayCode)}/seating`,
        {
          headers: new HttpHeaders({ Accept: 'application/json' }),
          withCredentials: false,
        }
      )
      .pipe(
        catchError((error: { status?: number }) =>
          error?.status === 404 ? of(null) : throwError(() => error)
        )
      );
  }

  /**
   * The seats a cart holds right now, nearest to lapsing first, with how many seconds each has left. The
   * server's clock, not the browser's, decides when a hold ends. [] for an empty or unknown cart.
   */
  public getSeatHolds(cartUid: string): Observable<SeatHold[]> {
    return this._http.get<SeatHold[]>(
      `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/seat-holds/${encodeURIComponent(cartUid)}`,
      { headers: new HttpHeaders({ Accept: 'application/json' }), withCredentials: false }
    );
  }

  public getTicketTypeAddOns(
    ticketTypeUid: string
  ): Observable<HttpResponse<TicketTypeAddOn[]>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<TicketTypeAddOn[]>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/ticket-types/${ticketTypeUid}/add-ons`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getCurrentSession(): Observable<HttpResponse<Session>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<Session>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/sessions/current-session`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getCartBySessionId(
    sessionId: number | string
  ): Observable<HttpResponse<Cart>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<Cart>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/session/${sessionId}/cart
`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getCartItemsByUuid(
    cartUid: string
  ): Observable<HttpResponse<CartItem[]>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<CartItem[]>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/cart/${cartUid}/items

`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public addToCart(
    cartReq: AddToCartRequest,
    cartUid: string
  ): Observable<HttpResponse<AddToCartResponse>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      responseType: 'text' as 'json',
    };
    return this._http
      .post<HttpResponse<AddToCartResponse>>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/cart/${cartUid}/add-items`,
        cartReq,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public checkout(
    email: string,
    cartUid: string,
    contactNumber: string,
    name: string,
    delivery = false,
    deliveryLocationUid?: string,
    address?: Address
  ): Observable<HttpResponse<Order>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    let url = `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/cart/${cartUid}/checkout?email=${email}&contactNumber=${contactNumber}&name=${name}`;

    // Non-delivery path is unchanged: no extra query params and a null body.
    // Delivery path adds the flag + location and sends the Address as the body.
    const body = delivery && deliveryLocationUid ? address ?? {} : null;
    if (delivery && deliveryLocationUid) {
      url += `&delivery=true&deliveryLocationUid=${deliveryLocationUid}`;
    }

    return this._http
      .post<Order>(url, body, httpOptions)
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getDeliveryLocations(): Observable<HttpResponse<DeliveryLocation[]>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };
    return this._http
      .get<DeliveryLocation[]>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/delivery-locations/all`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  // Initiate payment
  public initiatePayment(
    paymentReq: initiatePaymentReq,
    orderNumber: string
  ): Observable<HttpResponse<Order>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };
    return this._http
      .post<Order>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/orders/${orderNumber}/initiate-payment`,
        paymentReq,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  // Initiate a customer self-service ticket-wallet top-up. Authenticated: the token is added by
  // AuthInterceptor. Resolves the wallet from ticketUid (preferred) or ticketNumber server-side and
  // returns the order/payment in the same shape as initiate-payment, including the gateway
  // redirectUrl.
  public initiateWalletTopUp(
    payload: WalletTopUpRequest
  ): Observable<HttpResponse<Order>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };
    return this._http
      .post<Order>(
        // Authenticated route. This was /opn/v1/**, which is permitAll — anyone could start a
        // top-up against any ticket, and probe which tickets exist while doing it (QA OBS-3).
        // Requires the matching backend; the open path no longer exists.
        `${this.apiUrl}/${Microservice.ECOMMERCE}/client/v1/wallet-top-ups`,
        payload,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public getWalletTransactionsByTicketUid(
    ticketUid: string,
    page = 0,
    size = 20
  ): Observable<HttpResponse<PaginationResponse<MerchantPosTransaction[]>>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };
    return this._http
      .get<PaginationResponse<MerchantPosTransaction[]>>(
        `${this.apiUrl}/${Microservice.MERCHANTS}/client/v1/wallet-transactions?ticketUid=${ticketUid}&page=${page}&size=${size}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * The authenticated customer's balance for one of their own tickets (QA finding GAP-1 — customers
   * could fund a wallet and then never see it). Customer-scoped server-side: there is no email or
   * customer parameter, and a ticket belonging to someone else comes back 404.
   */
  public getMyTicketWalletBalance(
    ticketUid: string
  ): Observable<HttpResponse<TicketWallet>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };
    return this._http
      .get<TicketWallet>(
        `${this.apiUrl}/${Microservice.MERCHANTS}/client/v1/ticket-wallets?ticketUid=${ticketUid}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public clearCart(cartId: string): Observable<void> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
    };
    return this._http
      .post<void>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/cart/${cartId}/clear`,
        null,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Cancel a pending, unpaid order and release its held ticket inventory. The endpoint is open
   * (`opn`) so the cartUid the order was created under is sent as proof of ownership. The backend
   * rejects with 409 when the order is already paid/completed (leave it to reconciliation) and 404
   * when the cartUid does not own the order.
   */
  public cancelOrder(
    orderNumber: string,
    cartUid: string
  ): Observable<HttpResponse<Order>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };
    return this._http
      .post<Order>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/orders/${orderNumber}/cancel`,
        { cartUid },
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public removeCartItem(cartItemUid: string): Observable<HttpResponse<Cart>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
    };
    return this._http
      .post<HttpResponse<Cart>>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/cart-items/${cartItemUid}/remove`,
        null,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  // Get Event Registration Fields

  public getEventFieldsByEventUuid(
    uuid: string
  ): Observable<HttpResponse<EventField[]>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<EventField[]>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/events/${uuid}/registration-fields`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }
  // Get Event Fees

  public getOrderFeesByOrderNumber(
    orderNum: string
  ): Observable<HttpResponse<OrderFee[]>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<OrderFee[]>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/orders/${orderNum}/fees`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  // Get Orders by order Number
  public getOrdersByOrderNumber(
    orderNum: string
  ): Observable<HttpResponse<Order>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<Order>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/orders/${orderNum}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * One ticket by uid. Used by wallet top-up to read the ticket's own currency, which is the same
   * value ecommerce-service derives server-side. Deliberately not the ticket type's currency: a
   * free ticket type carries `currencyCode: null` while the ticket issued from it still records
   * one, so the type is the wrong source for anything that must always resolve.
   */
  public getTicketByUid(uid: string): Observable<HttpResponse<Ticket>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<Ticket>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/tickets/${uid}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  // Get tickets get order number
  public getTicketByOrderNumber(
    orderNum: string
  ): Observable<HttpResponse<Ticket[]>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<Ticket[]>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/orders/${orderNum}/tickets`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  // Get all my orders by email
  public getAllMyOrdersByEmail(
    email: string,
    sort?: string
  ): Observable<HttpResponse<Order[]>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),

      withCredentials: false,
      observe: 'response' as const,
    };

    let url = `${this.apiUrl}/${
      Microservice.ECOMMERCE
    }/cmn/v1/orders/my-orders/all?email=${encodeURIComponent(email)}`;
    if (sort) {
      url += `&sort=${encodeURIComponent(sort)}`;
    }

    return this._http.get<Order[]>(url, httpOptions).pipe(
      catchError((error: Error) => {
        return throwError(() => error);
      })
    );
  }

  // Get order by order number
  public getOrderByOrderNumber(
    orderNum: string
  ): Observable<HttpResponse<Order>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };

    return this._http
      .get<Order>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/orders/${orderNum}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  // Get items by order number
  public getItemsByOrderNumber(
    orderNum: string
  ): Observable<HttpResponse<any[]>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };

    return this._http
      .get<any[]>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/opn/v1/orders/${orderNum}/items`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  // Add item to wishlist
  public addToWishlist(request: {
    productUid: string;
    eventUid: string;
    ticketTypeUid: string;
    ticketTypeName: string;
    quantity: number;
  }): Observable<HttpResponse<WishlistItem>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };

    return this._http
      .post<WishlistItem>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/client/v1/wishlist`,
        request,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  // Remove item from wishlist
  public removeFromWishlist(
    wishlistItemUid: string
  ): Observable<HttpResponse<void>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: '*/*',
      }),
      observe: 'response' as const,
    };

    return this._http
      .delete<void>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/client/v1/wishlist/${wishlistItemUid}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  // Get all wishlist items
  public getMyWishlist(): Observable<HttpResponse<WishlistItem[]>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: '*/*',
      }),
      observe: 'response' as const,
    };

    return this._http
      .get<WishlistItem[]>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/client/v1/wishlist/my`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  // RSVP Endpoints

  public getRsvpDetails(token: string): Observable<HttpResponse<RsvpDetails>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .get<RsvpDetails>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/rsvp/${token}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public confirmRsvp(token: string): Observable<HttpResponse<RsvpDetails>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .post<RsvpDetails>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/rsvp/${token}/confirm`,
        {},
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public cancelRsvp(token: string): Observable<HttpResponse<RsvpDetails>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      withCredentials: false,
      observe: 'response' as const,
    };

    return this._http
      .post<RsvpDetails>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/rsvp/${token}/cancel`,
        {},
        httpOptions
      )
      .pipe(
        catchError((error: Error) => {
          return throwError(() => error);
        })
      );
  }

  public shareTicket(
    ticketUid: string,
    payload: { recipientEmail: string; recipientName: string; message: string }
  ): Observable<HttpResponse<void>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };

    return this._http
      .post<void>(
        `${this.apiUrl}/${Microservice.ETICKETING}/client/v1/tickets/${ticketUid}/share`,
        payload,
        httpOptions
      )
      .pipe(catchError((error: Error) => throwError(() => error)));
  }

  // Scroll to top - handles both window and scrollable container

  public scrollToTop(offset = 0): void {
    if (typeof window !== 'undefined') {
      try {
        // Try to scroll window first
        window.scrollTo({ left: 0, top: offset, behavior: 'smooth' });

        // Also scroll the main content container if it exists
        const mainElement = document.querySelector('main');
        if (mainElement) {
          mainElement.scrollTo({ left: 0, top: offset, behavior: 'smooth' });
        }
      } catch (error) {
        // Fallback for browsers that don't support smooth scrolling
        window.scrollTo(0, offset);
        const mainElement = document.querySelector('main');
        if (mainElement) {
          mainElement.scrollTop = offset;
        }
      }
    }
  }
}
